import { memo } from 'react';
import type { TFunction } from 'i18next';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import ShieldIcon from '@mui/icons-material/Shield';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import {
  Box,
  Button,
  Chip,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import type { WorkOrderDetail } from '@/hooks/useWorkOrders';
import {
  WAITING_APPROVAL_STATUS,
  approvalWaitLabel,
  showsApprovalWaitMarker,
} from '@/utils/workOrderStatusChips';
import { intlLocale } from '@/utils/locale';

interface WorkOrderStatusInfoProps {
  workOrder: WorkOrderDetail;
  t: TFunction;
  resolveDisputePending: boolean;
  resolveInvoicingHoldPending: boolean;
  onResolveDispute: () => void | Promise<void>;
  onResolveInvoicingHold: () => void | Promise<void>;
}

function WorkOrderStatusInfoInner({
  workOrder,
  t,
  resolveDisputePending,
  resolveInvoicingHoldPending,
  onResolveDispute,
  onResolveInvoicingHold,
}: WorkOrderStatusInfoProps) {
  const theme = useTheme();
  // Continuation plan indicator (#548): show whether paused work has an
  // expected resume time or is paused with no plan (operationally ambiguous).
  const isPaused = workOrder.status === 'paused';
  // "Customer waits" / "minor maintenance allowed" are schedule facts and live
  // on the schedule bar under the header, where they are also edited (#2105).
  // Derived approval wait (#3027): read-only, beside the status, no amounts.
  const showApprovalWait = showsApprovalWaitMarker(workOrder);
  const hasChips = !!(
    showApprovalWait ||
    workOrder.isWarranty ||
    workOrder.isDisputed ||
    workOrder.isInvoicingHold ||
    workOrder.isWaitingForParts ||
    isPaused
  );

  if (!hasChips && !workOrder.isDisputed && !workOrder.isInvoicingHold) {
    return null;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 3 }}>
      {hasChips && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {isPaused && (
            <Chip
              icon={<PauseCircleOutlineIcon />}
              label={
                workOrder.expectedResumeAt
                  ? t('workOrderFlags.pausedWithPlan', {
                      time: new Date(workOrder.expectedResumeAt).toLocaleString(intlLocale(), {
                        day: 'numeric',
                        month: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      }),
                    })
                  : t('workOrderFlags.pausedNoPlan')
              }
              color={workOrder.expectedResumeAt ? 'default' : 'warning'}
              variant={workOrder.expectedResumeAt ? 'filled' : 'outlined'}
              size="small"
            />
          )}
          {workOrder.isWarranty && (
            <Chip
              icon={<ShieldIcon />}
              label={t('workOrderFlags.warranty')}
              color="info"
              size="small"
            />
          )}
          {workOrder.isDisputed && (
            <Chip
              icon={<ReportProblemIcon />}
              label={t('workOrderFlags.disputed')}
              color="error"
              size="small"
            />
          )}
          {workOrder.isInvoicingHold && (
            <Chip
              icon={<HourglassBottomIcon />}
              label={t('workOrderFlags.invoicingHold')}
              color="warning"
              size="small"
            />
          )}
          {showApprovalWait && (
            <Chip
              icon={<PendingActionsIcon />}
              label={approvalWaitLabel(
                t(`status.${WAITING_APPROVAL_STATUS}`),
                workOrder.openApprovalCount,
              )}
              color="info"
              size="small"
              variant="outlined"
            />
          )}
          {workOrder.isWaitingForParts && (
            <Chip
              icon={<LocalShippingIcon />}
              label={t('workOrderFlags.waitingForParts')}
              color="warning"
              size="small"
              variant="outlined"
            />
          )}
        </Box>
      )}

      {workOrder.isDisputed && (
        <Box sx={{ p: 1.5, borderRadius: 1, bgcolor: alpha(theme.palette.error.main, 0.08), border: `1px solid ${alpha(theme.palette.error.main, 0.25)}` }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <ReportProblemIcon sx={{ color: 'error.main', fontSize: 18 }} />
            <Typography variant="subtitle2" sx={{ color: 'error.main' }}>
              {t('workOrderFlags.disputedDesc')}
            </Typography>
          </Box>
          {workOrder.disputeReason && (
            <Typography variant="body2" sx={{ mb: 1 }}>{workOrder.disputeReason}</Typography>
          )}
          <Button
            variant="contained"
            size="small"
            startIcon={<PlayArrowIcon />}
            onClick={() => { void onResolveDispute(); }}
            disabled={resolveDisputePending}
          >
            {t('workOrderActions.resolveDispute')}
          </Button>
        </Box>
      )}

      {workOrder.isInvoicingHold && (
        <Box sx={{ p: 1.5, borderRadius: 1, bgcolor: alpha(theme.palette.warning.dark, 0.08), border: `1px solid ${alpha(theme.palette.warning.dark, 0.25)}` }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <HourglassBottomIcon sx={{ color: 'warning.dark', fontSize: 18 }} />
            <Typography variant="subtitle2" sx={{ color: 'warning.dark' }}>
              {t('workOrderFlags.invoicingHoldDesc')}
            </Typography>
          </Box>
          {workOrder.invoicingHoldReason && (
            <Typography variant="body2" sx={{ mb: 1 }}>{workOrder.invoicingHoldReason}</Typography>
          )}
          <Button
            variant="contained"
            size="small"
            startIcon={<ReceiptLongIcon />}
            onClick={() => { void onResolveInvoicingHold(); }}
            disabled={resolveInvoicingHoldPending}
          >
            {t('workOrderActions.resolveInvoicingHold')}
          </Button>
        </Box>
      )}
    </Box>
  );
}

export const WorkOrderStatusInfo = memo(
  WorkOrderStatusInfoInner,
  (prev, next) =>
    prev.t === next.t &&
    prev.resolveDisputePending === next.resolveDisputePending &&
    prev.resolveInvoicingHoldPending === next.resolveInvoicingHoldPending &&
    prev.workOrder.id === next.workOrder.id &&
    prev.workOrder.status === next.workOrder.status &&
    prev.workOrder.expectedResumeAt === next.workOrder.expectedResumeAt &&
    prev.workOrder.isWarranty === next.workOrder.isWarranty &&
    prev.workOrder.isDisputed === next.workOrder.isDisputed &&
    prev.workOrder.isInvoicingHold === next.workOrder.isInvoicingHold &&
    prev.workOrder.isWaitingForParts === next.workOrder.isWaitingForParts &&
    prev.workOrder.isWaitingForApproval === next.workOrder.isWaitingForApproval &&
    prev.workOrder.openApprovalCount === next.workOrder.openApprovalCount &&
    prev.workOrder.disputeReason === next.workOrder.disputeReason &&
    prev.workOrder.invoicingHoldReason === next.workOrder.invoicingHoldReason,
);
